import { Stat } from "@/components/home/panel";
import { RankPanel } from "@/components/home/RankPanel";
import {
  ExceptionPanel,
  MutualLinkNotice,
  PresencePanel,
  RosterPanel,
} from "@/pages/home/blocks";
import { buildNeeds, NeedsPanel } from "@/pages/home/ProducerHome";
import { TodayCard } from "@/pages/home/TodayCard";
import type { ManagerData } from "@/pages/home/types";

/** For people whose team view comes from their direct reports — the lead
 *  brokers, who still produce themselves.
 *
 *  Team and self sit side by side rather than stacked: the previous version
 *  nested the entire ProducerHome underneath the team blocks, which pushed the
 *  page to roughly three screens and buried the roster.
 */
export function ManagerHome({
  data,
  allowed,
}: {
  data: ManagerData;
  allowed: Set<string>;
}) {
  const b = data.me;
  const needs = buildNeeds(data.needs, allowed);
  const steps = (data.onboarding?.steps ?? []).filter((s) => !s.done && s.cta_href);
  const mutualNames = data.roster
    .filter((r) => data.mutual_manager_ids.includes(r.user_id))
    .map((r) => r.full_name || r.email || "a teammate");

  return (
    <div className="space-y-3">
      {/* One KPI row covering both jobs: my output, then the team's. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <TodayCard b={b} />
        <Stat
          label="Team on target"
          value={data.team.on_target}
          suffix={`of ${data.team.producers}`}
          bar={data.team.producers ? data.team.on_target / data.team.producers : undefined}
          tone={data.team.on_target > 0 ? "emerald" : "amber"}
          foot="Reports at or above goal today"
        />
        <Stat
          label="Team published"
          value={data.team.published_today.toLocaleString()}
          foot="Across your reports today"
        />
      </div>

      {/* Left column is everything that needs doing today — the team's
          exceptions, then the manager's own list. Right column is reference:
          who reports to them and who is around. Grouping by "act" vs "check"
          also keeps the columns near the same height; with team blocks on the
          left and personal ones on the right, a quiet team left ~400px of
          dead space mid-page. */}
      <div className="grid gap-3 lg:grid-cols-3 items-start">
        <div className="lg:col-span-2 min-w-0 space-y-3">
          <ExceptionPanel
            rows={data.exceptions}
            title="Your team needs attention"
            emptyHint="Everyone is publishing and nothing is going cold."
          />
          <MutualLinkNotice names={mutualNames} />
          <NeedsPanel needs={needs} steps={steps} title="Your own list" />
        </div>
        <div className="space-y-3 min-w-0">
          <RosterPanel rows={data.roster} goalDaily={b.placement_goal_daily} />
          <RankPanel />
          <PresencePanel
            p={data.presence}
            to={allowed.has("/audit") ? "/audit?tab=people" : undefined}
          />
        </div>
      </div>
    </div>
  );
}
